import * as React from "react"
import { useUser } from "@clerk/clerk-react"
import { toast } from "sonner"

import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { createTrade } from "@/services/tradeApi"
import { frontendTradeToBackendTrade } from "@/services/tradeStorage"
import { resolveBrokerDisplayName } from "@/utils/customCsvAccountDisplay"
import { formDataToTrade } from "@/utils/tradeFormData"
import TradeForm from "@/views/Dashboard/TradeLog/components/TradeForm"
import type { TradeFormData } from "@/types/trade"
import { notifyTradesUpdated } from "@/hooks/useTradeStatistics"
import { presentMutationError, toastError } from "../../../../utils/showApiErrorToast";
import { safeRandomUUID } from "@/utils/safeRandomUUID";

const SECTION_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

const SECTION_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white"

export type ConnectBrokerManualEntryStepProps = {
  capability: BrokerCapabilities
}

export function ConnectBrokerManualEntryStep({
  capability,
}: ConnectBrokerManualEntryStepProps): React.ReactElement {
  const { user } = useUser()
  const [accountCode, setAccountCode] = React.useState("")
  const [isSaving, setIsSaving] = React.useState(false)
  const [attachmentStorageId] = React.useState(
    () => `pending-manual-${safeRandomUUID()}`
  )
  const brokerLabel = resolveBrokerDisplayName(capability)

  const handleSaveTrade = React.useCallback(
    async (formData: TradeFormData) => {
      const code = accountCode.trim()
      if (!code) {
        toastError("Account name required", {
          description: "Enter an account name before saving your first trade.",
        })
        return
      }
      if (!user?.id) {
        toastError("Not signed in", {
          description: "Sign in again to save trades.",
        })
        return
      }

      setIsSaving(true)
      try {
        const trade = formDataToTrade(formData, user.id)
        const payload = frontendTradeToBackendTrade(trade, code)
        await createTrade(payload)
        notifyTradesUpdated()
        toast.success("Trade saved", {
          description: `${formData.symbol} was added to ${code}. Add another row below or return to Broker Management.`,
        })
      } catch (error) {
        presentMutationError(error, "Save failed");
      } finally {
        setIsSaving(false)
      }
    },
    [accountCode, user?.id]
  )

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <p className="m-0 font-[family-name:var(--font-primary)] text-sm leading-normal text-gray-500 dark:text-gray-400">
        Add trades for {brokerLabel} one row at a time. Enter an account name to group these trades,
        then fill in each trade using the form below.
      </p>

      <Field className="w-full min-w-0 max-w-md">
        <FieldLabel htmlFor="manual-broker-account-code" className={SECTION_LABEL_CLASS}>
          Account name
        </FieldLabel>
        <Input
          id="manual-broker-account-code"
          value={accountCode}
          onChange={(event) => setAccountCode(event.target.value)}
          placeholder="e.g. Main live account"
          autoComplete="off"
        />
        <FieldDescription className={SECTION_DESC_CLASS}>
          This labels the account in Tessera. Use the same name when adding more trades later.
        </FieldDescription>
      </Field>

      <div className="rounded-xl border border-border p-4 md:p-6">
        <TradeForm
          attachmentStorageId={attachmentStorageId}
          onSubmit={handleSaveTrade}
          isLoading={isSaving}
        />
      </div>
    </div>
  )
}
